import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2, AlertCircle } from "lucide-react";

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

let googleMapsLoaded = false;
let googleMapsLoadPromise = null;
let googleMapsError = false;

function loadGoogleMaps() {
  if (googleMapsLoaded) return Promise.resolve();
  if (googleMapsError) return Promise.reject(new Error("Google Maps failed"));
  if (googleMapsLoadPromise) return googleMapsLoadPromise;
  googleMapsLoadPromise = new Promise((resolve, reject) => {
    if (window.google?.maps?.places) { googleMapsLoaded = true; resolve(); return; }
    // Suppress Google Maps error overlay
    const origConsoleError = console.error;
    window.gm_authFailure = () => { googleMapsError = true; };
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places&language=pt`;
    script.async = true;
    script.onload = () => {
      // Check if the API actually works (key might be restricted)
      setTimeout(() => {
        if (window.google?.maps?.places?.AutocompleteService) {
          googleMapsLoaded = true;
          resolve();
        } else {
          googleMapsError = true;
          reject(new Error("Google Maps Places not available"));
        }
      }, 500);
    };
    script.onerror = () => { googleMapsError = true; reject(new Error("Script load failed")); };
    document.head.appendChild(script);
  });
  return googleMapsLoadPromise;
}

export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, className, disabled, restaurantLat, restaurantLng, restaurantCity, restaurantCountry, "data-testid": testId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [ready, setReady] = useState(false);
  const [apiError, setApiError] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const loadingTimerRef = useRef(null);

  useEffect(() => {
    if (loading) {
      loadingTimerRef.current = setTimeout(() => { setLoading(false); setApiError(true); }, 4000);
    } else if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = null;
    }
    return () => { if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current); };
  }, [loading]);

  useEffect(() => {
    if (!GOOGLE_MAPS_KEY) { setApiError(true); return; }
    loadGoogleMaps().then(() => {
      if (googleMapsError) { setApiError(true); return; }
      try {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        placesService.current = new window.google.maps.places.PlacesService(document.createElement("div"));
        setReady(true);
      } catch (e) {
        setApiError(true);
      }
    }).catch(() => { setApiError(true); });
    const checkAuth = setInterval(() => {
      if (googleMapsError) { setApiError(true); clearInterval(checkAuth); }
    }, 1500);
    return () => clearInterval(checkAuth);
  }, []);

  // Hide Google Maps error overlays
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const el = node;
            if (el.style?.position === 'fixed' && el.innerText?.includes('Google Maps')) {
              el.style.display = 'none';
            }
            // Also check for the dismissButton / error container
            if (el.classList?.contains('dismissButton') || el.querySelector?.('.dismissButton')) {
              el.style.display = 'none';
            }
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchSuggestions = useCallback((input) => {
    if (!ready || apiError || !input || input.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const requestOptions = { input, types: ["address"], componentRestrictions: { country: ["pt", "es"] } };
      if (restaurantLat && restaurantLng) {
        requestOptions.locationBias = { center: { lat: restaurantLat, lng: restaurantLng }, radius: 20000 };
      } else if (restaurantCity) {
        requestOptions.input = `${input}, ${restaurantCity}`;
      }
      autocompleteService.current.getPlacePredictions(
        requestOptions,
        (predictions, status) => {
          setLoading(false);
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions.slice(0, 5));
            setShowDropdown(true);
          } else if (status === "REQUEST_DENIED") {
            setApiError(true);
            setSuggestions([]);
          } else {
            setSuggestions([]);
          }
        }
      );
    } catch {
      setLoading(false);
      setApiError(true);
    }
  }, [ready, apiError, restaurantLat, restaurantLng, restaurantCity]);

  // Nominatim fallback: geocode manually typed address
  const nominatimGeocode = useCallback(async (address) => {
    if (!address || address.length < 5) return;
    setLoading(true);
    try {
      const query = (restaurantLat && restaurantLng) || !restaurantCity
        ? address
        : `${address}, ${restaurantCity}`;
      let url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=pt,es&limit=5&addressdetails=1`;
      if (restaurantLat && restaurantLng) {
        url += `&viewbox=${restaurantLng - 0.3},${restaurantLat + 0.3},${restaurantLng + 0.3},${restaurantLat - 0.3}&bounded=0`;
      }
      const res = await fetch(url);
      const data = await res.json();
      setLoading(false);
      if (data && data.length > 0) {
        const formatted = data.map((r, i) => ({
          place_id: `nom_${i}`,
          description: r.display_name,
          structured_formatting: { main_text: r.display_name.split(",")[0], secondary_text: r.display_name.split(",").slice(1).join(",").trim() },
          _lat: parseFloat(r.lat),
          _lng: parseFloat(r.lon),
        }));
        setSuggestions(formatted);
        setShowDropdown(true);
      }
    } catch (e) {
      setLoading(false);
    }
  }, [restaurantLat, restaurantLng, restaurantCity]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    if (!apiError) {
      debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
    } else {
      debounceRef.current = setTimeout(() => nominatimGeocode(val), 500);
    }
  };

  const handleSelect = (prediction) => {
    setShowDropdown(false);
    setSuggestions([]);
    // Nominatim result (has _lat/_lng)
    if (prediction._lat && prediction._lng) {
      onChange(prediction.description);
      onSelect?.({ address: prediction.description, lat: prediction._lat, lng: prediction._lng });
      return;
    }
    // Google Maps result
    onChange(prediction.description);
    placesService.current.getDetails(
      { placeId: prediction.place_id, fields: ["geometry", "formatted_address"] },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry) {
          onSelect?.({
            address: place.formatted_address || prediction.description,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        }
      }
    );
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={value}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={apiError ? "Escribe tu direccion manualmente..." : (placeholder || "Buscar direccion...")}
          className={`pl-9 ${className || ""}`}
          disabled={disabled}
          data-testid={testId || "address-autocomplete"}
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
      </div>
      {apiError && (
        <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1" data-testid="address-manual-hint">
          <AlertCircle className="w-3 h-3" />
          Escribe tu direccion y selecciona de las sugerencias.
        </p>
      )}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-testid="address-suggestions">
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-start gap-2"
              onClick={() => handleSelect(s)}
              data-testid={`suggestion-${s.place_id}`}
            >
              <MapPin className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
              <div>
                <span className="font-medium text-gray-900">{s.structured_formatting?.main_text}</span>
                <span className="text-gray-500 text-xs block">{s.structured_formatting?.secondary_text}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
